// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Token Storage Module
 *
 * Centralized localStorage management for user profile data.
 * JWT tokens are managed by MSAL.js (stored in its own localStorage keys).
 * This module handles the user data hydrated from the /api/v1/me endpoint.
 */

import { STORAGE_KEYS } from '../utils/constants';
import type { UserData } from '../types';
import { useAlarmWorkOrderStore } from '../stores/alarmWorkOrderStore';

/**
 * Store user data (from /me response) in localStorage
 */
export const setUserData = (data: UserData): void => {
  localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(data));
};

/**
 * Get stored user data from localStorage
 */
export const getUserData = (): UserData | null => {
  const raw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserData;
  } catch {
    return null;
  }
};

/**
 * Remove stored user data from localStorage — and the alarm↔work-order links
 * this browser kept for that user, on every path that ends a session (logout,
 * a 401, expiry). The links hold alarm labels and paths, and the store is per
 * browser, not per user, so the next person to sign in must not inherit them.
 * Resetting the store clears both the in-memory map and its persisted copy.
 */
export const removeUserData = (): void => {
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  useAlarmWorkOrderStore.setState({ byAlarmId: {} });
};
